import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {WorkflowStep} from "../workflow-step";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {OrderService} from "../../order/order.service";
import {OrderWorkflowService} from "./order-workflow.service";
import {ConfirmDialogService} from "../../confirm-dialog/confirm-dialog-service";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {OrderFormGqlModel} from "../../order/order-form.gql-model";
import {ProjectService} from "../../project/project.service";
import { AppSettingsService } from "../../app-settings/app-settings.service";
import { HasuraGraphqlService } from "../../graphql/hasura-graphql-service";

@Component({
  templateUrl: "./confirm-order.template.pug",
  providers: [OrderService, OrderWorkflowService, ConfirmDialogService, HasuraGraphqlService],
})
export class ConfirmOrderComponent extends WorkflowStep {
  error: Error;

  canEdit: boolean;
  sent: boolean = false;
  canCancelOrder: boolean = false;
  orderForm: OrderFormGqlModel = new OrderFormGqlModel({});
  itsmAddress: any = {
    leasingPartner: {},
    endCustomer: {},
  };

  editableFields: {[key: string]: boolean} = {
    deliveryContact: false,
    installationContact: false,
    siteAddress: false,
    alternateAddress: false,
    configurationAddress: false,
    shippingDestination: false,
    deliveryMethod: false,
    shipCompleted: false,
    floorLevel: false,
    resellerInformation: false,
    resellerInstructions: false,
    sourceDocumentationLink: false,
    deliveryInformation: false,
    orderNotificationEmailList: false,
    targetDeliveryDate: false,
    estimatedDeliveryDate: false,
    estimatedHardwareOrderDate: false,
    requestedDeliveryDate: false,
  };

  @toggleLoading()
  @handleErrors()
  async ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    const orderRequested = this.workflowService.workflowConfig.target["orderRequested"];
    this.canCancelOrder = this.workflowService.userHasPrivilege("transition-lead") && !orderRequested;
    this.canEdit = !orderRequested;

    if (this.canEdit && !this.isLocked) {
      Object.keys(this.editableFields).forEach((field) => { this.editableFields[field] = true; });
    }
    // REQUIRES - this.projectId to be set
    // Must be called before the main call to retrieve the project
    let enableApolloProjectQuery = await this.appSettingsService.getFeaturePermission(
      "enableApolloProjectQuery",
      this.workflowService.workflowConfig.projectId,
    );

    let params: any = `
    id
    endUserAddress: endUserAddress: addressByEndUserAddressId {
      id
      name1
      name2
      line1
      line2
      city
      region
      postalCode: postal_code
      country {
        country_name
        created_at
        geo_region_id
        updated_at
      }
      customShippingLabel1: custom_label_1
      customShippingLabel2: custom_label_2
      deliveryType: delivery_type
      businessPartnerId: business_partner_id
      itsmSiteId: itsm_site_id
      itsmSiteName: itsm_site_name
      itsmBuildingId: itsm_building_id
      itsmBuilding: itsm_building
      buildingTypes: building_types
    }
    billToAddress: address {
      id
      name1
      name2
      line1
      line2
      city
      region
      postalCode: postal_code
      country {
        country_name
        created_at
        geo_region_id
        updated_at
      }
      customShippingLabel1: custom_label_1
      customShippingLabel2: custom_label_2
      deliveryType: delivery_type
      businessPartnerId: business_partner_id
      itsmSiteId: itsm_site_id
      itsmSiteName: itsm_site_name
      itsmBuildingId: itsm_building_id
      itsmBuilding: itsm_building
      buildingTypes: building_types
    }`;
    const { endUserAddress, billToAddress } =
    await this.hasuraGqlService.getProjectResponseFromHasura(this.workflowService.workflowConfig.projectId, params);

    this.itsmAddress = {endCustomer: {...endUserAddress}, leasingPartner: {...billToAddress}};
    this.orderForm = await this.orderService.getOrderForm(workflowTargetInfo.targetId);
  }

  @handleErrors()
  async autoSave(): Promise<boolean> {
    if (!this.orderForm.header.orderAcceptanceCompleted) {
      return false;
    }

    await this.orderService.confirmOrderForm(this.workflowTargetInfo.targetId);

    await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

    return true;
  }

  async submitOrder(id): Promise<void> {
    await this.orderService.confirmOrderForm(id);
  }

  @handleErrors()
  async notifyOrderAcceptance() {
    const result = await this.confirmDialogService.confirm(
      "Confirm",
      "Send notification to the contract administrator?",
    );

    if (result) {
      await this.orderWorkflowService.notifyOrderAcceptance(this.workflowTargetInfo.targetId);

      this.sent = true;
    }
  }

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public orderService: OrderService,
    public orderWorkflowService: OrderWorkflowService,
    private confirmDialogService: ConfirmDialogService,
    public projectService: ProjectService,
    private appSettingsService: AppSettingsService,
    private hasuraGqlService: HasuraGraphqlService,
  ) {
    super(true);
    this.saveOnContinue = true;
  }
}
